import { HistorySource } from '../../components/HistorySource';
import './PrintReportPage.css';
import { CurrentCashComparison } from './cash/CurrentCashComparison';
import { AccountingOverviewSummary } from './AccountingOverviewSummary';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ActionKey,
  type AccountingHistoryPreset,
  type AccountingHistoryResponse,
  type AccountingValue,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { formatAccountingInstant } from '../../lib/accounting-time';
import { sessionVenueKey, useAuthStore } from '../../store/auth.store';
import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
import { gameRows, pendingMembers } from './accountingReportModel';

const button = 'min-h-11 border border-base-300 rounded px-3 py-2 disabled:opacity-50';
const presets: AccountingHistoryPreset[] = ['current_week', 'previous_week', 'current_month'];
export default function PrintReportPage() {
  const actor = useAuthStore((s) => s.user?.id),
    token = useAuthStore((s) => s.accessToken),
    revision = useAuthStore((s) => s.sessionRevision);
  const venue = sessionVenueKey(token);
  return (
    <Overview
      key={`${actor}:${venue}:${revision}`}
      scope={`${actor}:${venue}:${revision}`}
      venue={venue}
    />
  );
}
function Overview({ scope, venue }: { scope: string; venue: string }) {
  const { t, i18n } = useTranslation('accounting');
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [printing, setPrinting] = useState(false),
    [printError, setPrintError] = useState(false);
  const printGate = useRef(false),
    activePrint = useRef<AbortController>();
  useEffect(() => () => activePrint.current?.abort(), []);
  const canRead = useAuthStore(
    (s) => !!s.user?.isSystemUser || s.allowedActions?.includes(ActionKey.ACCOUNTING_READ),
  );
  const canMembers = useAuthStore(
    (s) => !!s.user?.isSystemUser || s.allowedActions?.includes(ActionKey.MEMBER_READ),
  );
  const canEvents = useAuthStore(
    (s) =>
      !!s.user?.isSystemUser || !s.visiblePages?.length || s.visiblePages.includes('/tournament'),
  );
  const canSettle = useAuthStore(
    (s) =>
      !!s.user?.isSystemUser ||
      !s.visiblePages?.length ||
      s.visiblePages.includes('/accounting/settle'),
  );
  const view =
    params.get('view') ?? (params.has('sessionId') || params.has('date') ? 'history' : 'today');
  const preset =
    params.get('preset') ??
    (view === 'history' && !params.has('sessionId') && !params.has('date')
      ? 'current_week'
      : undefined);
  const date = params.get('date') ?? undefined,
    sessionId = params.get('sessionId') ?? undefined;
  const valid =
    ['today', 'history'].includes(view) &&
    (!preset || presets.includes(preset as AccountingHistoryPreset)) &&
    !(preset && (date || sessionId)) &&
    (!date || /^\d{4}-\d{2}-\d{2}$/.test(date)) &&
    !(view === 'today' && (preset || date || sessionId));
  const [selected, setSelected] = useState<string>(),
    [page, setPage] = useState(1),
    [journal, setJournal] = useState<string>(),
    [technical, setTechnical] = useState(false);
  const selectors = { preset, date, sessionId };
  const query = useQuery({
    queryKey: ['accounting-history', scope, view, preset, date, sessionId],
    queryFn: ({ signal }) =>
      api.get<AccountingHistoryResponse>('/accounting/history', { signal, params: selectors }),
    enabled: !!canRead && !!venue && valid,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
  const data =
    !query.isError && !query.isFetching && query.data?.venueId === venue ? query.data : undefined;
  const day =
    selected === ''
      ? undefined
      : selected && data?.days.some((d) => d.businessDate === selected)
        ? selected
        : data?.days.length === 1
          ? data.days[0]?.businessDate
          : undefined;
  const detailQuery = useQuery({
    queryKey: ['accounting-history-detail', scope, selectors, data?.sourceVersion, day, page],
    queryFn: ({ signal }) =>
      api.get<AccountingHistoryResponse>(`/accounting/history/${day}`, {
        signal,
        params: {
          ...selectors,
          sourceVersion: data!.sourceVersion,
          asOf: data!.asOf,
          page,
          limit: 20,
        },
      }),
    enabled: !!canRead && !!data && !!day && !query.isFetching,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
  const detail =
    !query.isFetching &&
    !detailQuery.isFetching &&
    !detailQuery.isError &&
    detailQuery.data?.venueId === venue &&
    detailQuery.data.sourceVersion === data?.sourceVersion
      ? detailQuery.data.detail
      : undefined;
  const refetchHistory = query.refetch;
  const staleRecovery = useRef<{ selection: string; attempted: boolean }>({
    selection: '',
    attempted: false,
  });
  const recoverySelection = JSON.stringify([view, preset, date, sessionId, selected]);
  useEffect(() => {
    const error = detailQuery.error as { response?: { status?: number } } | null;
    if (staleRecovery.current.selection !== recoverySelection)
      staleRecovery.current = { selection: recoverySelection, attempted: false };
    // A successful detail read ends the recovery episode. A new main response alone
    // does not: sourceVersion also contains asOf and changes on every refresh.
    if (detail) staleRecovery.current.attempted = false;
    if (error?.response?.status !== 409 || !data || staleRecovery.current.attempted) return;
    staleRecovery.current.attempted = true;
    setPage(1);
    void refetchHistory();
  }, [detailQuery.error, data, detail, recoverySelection, refetchHistory]);
  const selection = useRef('');
  selection.current = JSON.stringify([view, selectors, day, page, data?.sourceVersion]);
  async function printSnapshot() {
    if (printGate.current || !data || !canRead) return;
    printGate.current = true;
    setPrinting(true);
    setPrintError(false);
    const key = selection.current,
      controller = new AbortController();
    activePrint.current = controller;
    try {
      const verified = await api.get<AccountingHistoryResponse>(
        day ? `/accounting/history/${day}` : '/accounting/history',
        {
          signal: controller.signal,
          params: {
            ...selectors,
            asOf: data.asOf,
            sourceVersion: data.sourceVersion,
            ...(day ? { page, limit: 20 } : {}),
          },
        },
      );
      const auth = useAuthStore.getState();
      if (
        controller.signal.aborted ||
        scope !== `${auth.user?.id}:${sessionVenueKey(auth.accessToken)}:${auth.sessionRevision}` ||
        selection.current !== key
      )
        return;
      if (
        verified.venueId !== venue ||
        verified.sourceVersion !== data.sourceVersion ||
        verified.asOf !== data.asOf ||
        (day && verified.detail?.businessDate !== day)
      )
        throw Error('REPORT_SOURCE_CHANGED');
      window.print();
    } catch {
      if (!controller.signal.aborted && selection.current === key) setPrintError(true);
    } finally {
      printGate.current = false;
      if (!controller.signal.aborted) setPrinting(false);
    }
  }
  const instant = (value: string | null | undefined) => {
    if (!value || !data) return t('overview.unavailable');
    const precision = technical ? 'second' : 'minute';
    const full = formatAccountingInstant(value, data.displayTimezone, i18n.language, { precision });
    const screen = formatAccountingInstant(value, data.displayTimezone, i18n.language, {
      precision,
      businessDate: view === 'today' ? data.range.start : undefined,
    });
    return (
      <time dateTime={value}>
        <span className="print:hidden">{screen ?? t('overview.unavailable')}</span>
        <span className="hidden print:inline">{full ?? t('overview.unavailable')}</span>
      </time>
    );
  };
  /** Number first; the status shows only when the figure is not complete. */
  const amount = (value?: AccountingValue) => (
    <span>
      <span className="whitespace-nowrap">{value?.value ?? t('overview.unavailable')}</span>
      {value?.reasonCode && (
        <small className="block text-base-content/60">
          {t(`dataReasons.${value.reasonCode}`, { defaultValue: t('overview.unavailable') })}
        </small>
      )}
      {value && value.status !== 'READY' && value.value !== null && (
        <small className="block text-warning">{t(`overview.${value.status}`)}</small>
      )}
    </span>
  );
  const navigate = (nextView: string, nextPreset?: string) => {
    activePrint.current?.abort();
    setPrinting(false);
    setPrintError(false);
    setSelected(undefined);
    setPage(1);
    setParams(nextPreset ? { view: nextView, preset: nextPreset } : { view: nextView });
  };
  const refresh = () => {
    staleRecovery.current.attempted = false;
    setPage(1);
    setPrintError(false);
    void query.refetch();
    void qc.invalidateQueries({ queryKey: ['accounting-history-detail', scope] });
  };
  if (!canRead)
    return (
      <AdminPage header={<AdminPageHeader title={t('overview.title')} />}>
        {' '}
        <p role="alert">{t('dailyLedgers.denied')}</p>{' '}
      </AdminPage>
    );
  const selectedDay = data?.days.find((d) => d.businessDate === day);
  const games = detail ? gameRows(detail.settlements) : [];
  const pending = detail ? pendingMembers(detail.settlements) : 0;
  const activeGroup = detail?.groups.find((g) => g.key === journal) ?? detail?.groups[0];
  const cell =
    'p-2 whitespace-nowrap print:whitespace-normal print:break-words print:px-1 print:py-1';

  return (
    <AdminPage className="accounting-page accounting-overview space-y-5">
      <style>{'@media print { @page { size: A4 landscape; margin: 8mm; } }'}</style>
      <AdminPageHeader
        title={t('overview.title')}
        description={
          data && (
            <>
              {data.range.start} — {data.range.end} · {instant(data.asOf)}
            </>
          )
        }
        actions={
          <div className="flex gap-2 print:hidden">
            <button className={button} onClick={refresh} disabled={query.isFetching || !valid}>
              {t('dailyLedgers.refresh')}
            </button>
            <button
              className={button}
              disabled={
                printing ||
                printError ||
                !data ||
                query.isFetching ||
                detailQuery.isFetching ||
                (!!day && !detail)
              }
              onClick={() => void printSnapshot()}
            >
              {t('printReport')}
            </button>
          </div>
        }
      />
      {data &&
        day === data.serverBusinessDate &&
        selectedDay?.status === 'OPEN' &&
        selectedDay.sessionId &&
        !params.has('asOf') && (
          <CurrentCashComparison
            key={`${scope}:${selectedDay.sessionId}:${day}`}
            sessionId={selectedDay.sessionId}
            venue={venue}
            scope={scope}
          />
        )}
      <nav aria-label={t('overview.view')} className="flex flex-wrap gap-2 print:hidden">
        <button
          className={button}
          aria-pressed={view === 'today'}
          onClick={() => navigate('today')}
        >
          {t('overview.today')}
        </button>
        <button
          className={button}
          aria-pressed={view === 'history'}
          onClick={() => navigate('history', 'current_week')}
        >
          {t('overview.history')}
        </button>
        {view === 'history' &&
          presets.map((p) => (
            <button
              className={button}
              aria-pressed={preset === p}
              key={p}
              onClick={() => navigate('history', p)}
            >
              {t(`overview.${p}`)}
            </button>
          ))}
        <label className="ml-auto inline-flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={technical}
            onChange={(e) => setTechnical(e.target.checked)}
          />
          {t('overview.showTechnical')}
        </label>
      </nav>
      {!valid && <p role="alert">{t('overview.invalid')}</p>}
      {printError && <p role="alert">{t('overview.stale')}</p>}
      {query.isFetching && <p role="status">{t('dailyLedgers.loading')}</p>}
      {query.isError && <p role="alert">{t('overview.failed')}</p>}
      {data && (
        <>
          {technical && (
            <p className="break-all text-xs text-base-content/60">
              {t('overview.sourceVersion')}: {data.sourceVersion}
            </p>
          )}
          <AccountingOverviewSummary
            data={data}
            selectedDate={day}
            onSelectDay={(businessDate) => {
              setSelected(businessDate);
              setPage(1);
            }}
          />
          {day && (
            <section
              aria-label={t('overview.detail')}
              className="space-y-5 border border-base-300 p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-xl font-semibold">
                  {day} · {t('overview.detail')}
                  {selectedDay && (
                    <small className="ml-2 text-base font-normal">
                      {t(`overview.${selectedDay.status}`)}
                    </small>
                  )}
                </h2>
                <button
                  className={`${button} print:hidden`}
                  onClick={() => {
                    setSelected('');
                    setPage(1);
                  }}
                >
                  {t('overview.close')}
                </button>
              </div>
              {detailQuery.isFetching && <p role="status">{t('dailyLedgers.loading')}</p>}
              {detailQuery.isError && <p role="alert">{t('overview.stale')}</p>}
              {detail && (
                <>
                  {selectedDay?.status === 'OPEN' && (
                    <p className="border border-base-300 p-3">
                      {t('overview.nextSteps')}
                      {pending > 0 && (
                        <strong className="ml-2 text-warning">
                          {t('overview.pendingMembers', { count: pending })}
                        </strong>
                      )}
                      {canSettle && (
                        <Link className="link ml-2 print:hidden" to="/accounting/settle">
                          {t('overview.openSettle')}
                        </Link>
                      )}
                    </p>
                  )}
                  {(!!detail.unclassifiedIncome?.length ||
                    !!detail.lateEvidence?.length ||
                    !!detail.historicalSettlementRows?.length ||
                    detail.settlements.some((s) => s.reasons.length > 0) ||
                    detail.groups.some((g) => g.status !== 'READY' || g.warnings.length > 0)) && (
                    <p role="status" className="text-warning">
                      {t('overview.evidenceWarning')}
                    </p>
                  )}
                  {!!detail.operationalCompletions?.length && (
                    <section aria-label={t('overview.operationalCompletions')}>
                      <h3 className="font-semibold">{t('overview.operationalCompletions')}</h3>
                      <p className="text-sm text-base-content/60">
                        {t('overview.noPostingNotice')}
                      </p>
                      <ul className="list-inside list-disc">
                        {detail.operationalCompletions.map((event) => (
                          <li key={event.tournamentId}>
                            {canEvents ? (
                              <Link className="link" to={`/tournament/${event.tournamentId}`}>
                                {event.sessionLabel}
                              </Link>
                            ) : (
                              event.sessionLabel
                            )}
                            {' · '}
                            {instant(event.completedAt)}
                          </li>
                        ))}
                      </ul>
                    </section>
                  )}
                  <section>
                    <h3 className="font-semibold">{t('overview.settlements')}</h3>
                    <p className="text-sm text-base-content/60">
                      {t('overview.confirmationNotice')}
                    </p>
                    {!games.length ? (
                      <p>{t('noData')}</p>
                    ) : (
                      <div className="overflow-x-auto print:overflow-visible">
                        <table
                          className="w-full min-w-[960px] text-left text-sm print:min-w-0 print:table-fixed print:text-[9px]"
                          aria-label={t('overview.settlements')}
                        >
                          <thead>
                            <tr>
                              {[
                                'session',
                                'status',
                                'people',
                                'raCount',
                                'venueFee',
                                'expenseTotal',
                                'venueFeeNet',
                                'activityFee1',
                                'activityFee2',
                                'retainedZ',
                              ].map((k) => (
                                <th className={cell} key={k}>
                                  {t(`overview.${k}`)}
                                </th>
                              ))}
                              <th className={cell}>{t('overview.netAward')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {games.map((g) => (
                              <tr className="border-t border-base-300" key={g.tournamentId}>
                                <td className={cell}>
                                  {canEvents ? (
                                    <Link className="link" to={`/tournament/${g.tournamentId}`}>
                                      {g.sessionLabel}
                                    </Link>
                                  ) : (
                                    g.sessionLabel
                                  )}
                                </td>
                                <td className={cell}>
                                  {t(`overview.${g.status}`)}
                                  {g.unverified && (
                                    <small className="block text-warning">
                                      {t('overview.sourceUnverified')}
                                    </small>
                                  )}
                                </td>
                                {g.available ? (
                                  <>
                                    <td className={cell}>{g.people}</td>
                                    <td className={cell}>
                                      {g.raCount ?? t('overview.unavailable')}
                                    </td>
                                    <td className={cell}>{amount(g.venueFee)}</td>
                                    <td className={cell}>{amount(g.expenseTotal)}</td>
                                    <td className={cell}>{amount(g.venueFeeNet)}</td>
                                    <td className={cell}>{amount(g.activityFee1)}</td>
                                    <td className={cell}>{amount(g.activityFee2)}</td>
                                    <td className={cell}>{amount(g.retainedZ)}</td>
                                    <td className={cell}>{g.netAward} TWD</td>
                                  </>
                                ) : (
                                  // Not a valid confirmation: the figures are unknown, not zero.
                                  <td className={`${cell} text-base-content/60`} colSpan={9}>
                                    {t('overview.unavailable')}
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </section>
                  <section className="space-y-2">
                    <h3 className="font-semibold">{t('overview.journals')}</h3>
                    <div role="tablist" className="flex flex-wrap gap-2 print:hidden">
                      {detail.groups.map((g) => (
                        <button
                          key={g.key}
                          role="tab"
                          id={`journal-tab-${g.key}`}
                          aria-controls="journal-panel"
                          aria-selected={activeGroup?.key === g.key}
                          className={
                            activeGroup?.key === g.key
                              ? `${button} border-primary font-semibold text-primary`
                              : button
                          }
                          onClick={() => setJournal(g.key)}
                        >
                          {t(`dailyLedgers.groups.${g.key}`)} ({g.total})
                          {g.status !== 'READY' && (
                            <span className="ml-1 text-warning">· {t(`overview.${g.status}`)}</span>
                          )}
                        </button>
                      ))}
                    </div>
                    {activeGroup && (
                      <div
                        role="tabpanel"
                        id="journal-panel"
                        aria-labelledby={`journal-tab-${activeGroup.key}`}
                        className="space-y-2"
                      >
                        <p className="text-sm">
                          {t(`dailyLedgers.groups.${activeGroup.key}`)} · {activeGroup.unit} ·{' '}
                          {t(
                            activeGroup.key === 'ZIPA' &&
                              activeGroup.warnings.includes('ZIPA_HISTORY_WITHOUT_LEDGER_ANCHOR')
                              ? 'dailyLedgers.zipaUnknownTotals'
                              : 'overview.groupTotals',
                            {
                              opening: activeGroup.opening ?? '—',
                              increases: activeGroup.increases ?? '—',
                              decreases: activeGroup.decreases ?? '—',
                              closing: activeGroup.closing ?? '—',
                              total: activeGroup.total,
                            },
                          )}
                          {activeGroup.status !== 'READY' && (
                            <span className="ml-2 text-warning">
                              {t(`overview.${activeGroup.status}`)}
                            </span>
                          )}
                        </p>
                        {activeGroup.warnings.map((warning) => (
                          <p key={warning} role="status" className="text-sm">
                            {t(`dailyLedgers.warnings.${warning}`, {
                              defaultValue: t('dailyLedgers.unavailable'),
                            })}
                          </p>
                        ))}
                        {activeGroup.key === 'ZIPA' &&
                          activeGroup.total === 0 &&
                          activeGroup.warnings.includes('ZIPA_HISTORY_WITHOUT_LEDGER_ANCHOR') && (
                            <p role="status">{t('dailyLedgers.zipaNoRecordedMovements')}</p>
                          )}
                        {activeGroup.key === 'PETTY' || activeGroup.key === 'MEMBERSHIP' ? (
                          <div className="overflow-x-auto">
                            <table
                              className="w-full text-left text-sm"
                              aria-label={t('overview.journals')}
                            >
                              <thead>
                                <tr>
                                  {(activeGroup.key === 'PETTY'
                                    ? ['operationTime', 'amount', 'note']
                                    : ['postingTime', 'memberNo', 'nickname', 'amount', 'note']
                                  ).map((k) => (
                                    <th className="p-2" key={k}>
                                      {t(`overview.${k}`)}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {activeGroup.rows.map((r) => (
                                  <tr className="border-t border-base-300" key={r.id}>
                                    <td className="p-2 whitespace-nowrap">{instant(r.postedAt)}</td>
                                    {activeGroup.key === 'MEMBERSHIP' && (
                                      <>
                                        <td className="p-2">
                                          {r.metadata?.memberNo ?? t('overview.unavailable')}
                                        </td>
                                        <td className="p-2">
                                          {canMembers && r.memberHref ? (
                                            <Link className="link" to={r.memberHref}>
                                              {r.metadata?.memberName ??
                                                r.memberName ??
                                                t('overview.unresolvedName')}
                                            </Link>
                                          ) : (
                                            (r.metadata?.memberName ??
                                            r.memberName ??
                                            t('overview.unresolvedName'))
                                          )}
                                          {r.metadata?.memberReferenceEvidence && (
                                            <small className="block text-base-content/60">
                                              {t(`overview.${r.metadata.memberReferenceEvidence}`)}
                                            </small>
                                          )}
                                        </td>
                                      </>
                                    )}
                                    <td className="p-2 whitespace-nowrap">
                                      {r.amount ?? '—'} {r.unit}
                                    </td>
                                    <td className="p-2">
                                      <HistorySource
                                        source={r.metadata?.displaySource}
                                        note={r.metadata?.note}
                                        technical={technical}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="overflow-x-auto">
                            <table
                              className="w-full min-w-[720px] text-left text-sm"
                              aria-label={t('overview.journals')}
                            >
                              <thead>
                                <tr>
                                  {[
                                    'eventTime',
                                    'session',
                                    'member',
                                    'amount',
                                    'afterBalance',
                                    'note',
                                  ].map((k) => (
                                    <th className="p-2" key={k}>
                                      {t(`overview.${k}`)}
                                    </th>
                                  ))}
                                  {technical && <th className="p-2">{t('overview.origin')}</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {activeGroup.rows.map((r) => {
                                  const name = r.metadata?.memberName ?? r.memberName;
                                  const label =
                                    `${r.metadata?.memberNo ?? ''} ${name ?? t('overview.unresolvedName')}`.trim();
                                  return (
                                    <tr className="border-t border-base-300" key={r.id}>
                                      <td className="p-2 whitespace-nowrap">
                                        {instant(r.metadata?.eventTime ?? r.postedAt)}
                                      </td>
                                      <td className="p-2">
                                        {canEvents && r.metadata?.tournamentHref ? (
                                          <Link className="link" to={r.metadata.tournamentHref}>
                                            {r.metadata.displaySource?.name ??
                                              t('common:historySource.unavailable')}
                                          </Link>
                                        ) : (
                                          (r.metadata?.displaySource?.name ??
                                          t('common:historySource.unavailable'))
                                        )}
                                        {r.metadata?.levelLabel && (
                                          <small className="block text-base-content/60">
                                            {r.metadata.levelLabel}
                                          </small>
                                        )}
                                      </td>
                                      <td className="p-2">
                                        {canMembers && r.memberHref ? (
                                          <Link className="link" to={r.memberHref}>
                                            {label}
                                          </Link>
                                        ) : (
                                          label
                                        )}
                                        {r.metadata?.memberType?.label && (
                                          <small className="block text-base-content/60">
                                            {r.metadata.memberType.label}
                                          </small>
                                        )}
                                      </td>
                                      <td className="p-2 whitespace-nowrap">
                                        {r.amount ?? '—'} {r.unit}
                                        {r.metadata?.origin?.startsWith('MANUAL_') && (
                                          <small className="block">
                                            {t(`overview.${r.metadata.origin}`)}
                                          </small>
                                        )}
                                      </td>
                                      <td className="p-2 whitespace-nowrap">
                                        {r.metadata?.afterBalance ?? '—'}
                                        {r.metadata?.afterBalanceStatus &&
                                          r.metadata.afterBalanceStatus !== 'VERIFIED_PREFIX' && (
                                            <small className="block text-warning">
                                              {t(`overview.${r.metadata.afterBalanceStatus}`)}
                                            </small>
                                          )}
                                      </td>
                                      <td className="p-2">
                                        <HistorySource
                                          source={r.metadata?.displaySource}
                                          note={r.metadata?.note}
                                          technical={technical}
                                        />
                                        {r.metadata?.origin?.startsWith('MANUAL_') && (
                                          <small className="block">
                                            {t('overview.operator')}:{' '}
                                            {r.metadata.actorName ??
                                              t('common:historySource.actorUnavailable')}
                                          </small>
                                        )}
                                        {technical && r.metadata?.correctionReason && (
                                          <small className="block text-warning">
                                            {t('overview.correctionReason')}:{' '}
                                            {r.metadata.correctionReason}
                                          </small>
                                        )}
                                      </td>
                                      {technical && (
                                        <td className="p-2 text-xs">
                                          {t(`overview.${r.metadata?.origin ?? 'UNKNOWN'}`)} ·{' '}
                                          {t(
                                            `overview.${r.metadata?.memberReferenceEvidence ?? 'UNAVAILABLE'}`,
                                          )}
                                          <small className="block break-all">
                                            {r.sourceId} · v{r.sourceVersion ?? '—'}
                                          </small>
                                          <small className="block">{instant(r.postedAt)}</small>
                                          {r.metadata?.correctionOf && (
                                            <small className="block break-all">
                                              {t('overview.originalReceipt')}:{' '}
                                              {r.metadata.correctionOf}
                                            </small>
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                  <div className="flex flex-wrap items-center gap-2 print:hidden">
                    <button
                      className={button}
                      disabled={page <= 1 || detailQuery.isFetching}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      {t('overview.previous')}
                    </button>
                    <button
                      className={button}
                      disabled={
                        detailQuery.isFetching ||
                        Math.max(
                          detail.totals.income,
                          detail.totals.receipts,
                          detail.totals.historicalSettlementRows ?? 0,
                          detail.totals.unclassifiedIncome ?? 0,
                          ...detail.groups.map((g) => g.total),
                        ) <=
                          page * detail.limit
                      }
                      onClick={() => setPage((p) => p + 1)}
                    >
                      {t('overview.next')}
                    </button>
                    <span className="text-sm text-base-content/60">
                      {t('overview.pageScope', { page, limit: detail.limit })}
                    </span>
                  </div>
                  <details className="border border-base-300 p-3" open={technical}>
                    <summary className="cursor-pointer font-semibold">
                      {t('overview.auditDetails')}
                    </summary>
                    <div className="mt-3 space-y-5">
                      <section>
                        <h3 className="font-semibold">{t('overview.receipts')}</h3>
                        <p className="text-sm">{t('overview.receiptNotice')}</p>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr>
                                {['direction', 'amount', 'time', 'source'].map((k) => (
                                  <th className="p-2 text-left" key={k}>
                                    {t(`overview.${k}`)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {detail.receipts.map((r) => (
                                <tr className="border-t border-base-300" key={r.id}>
                                  <td>{t(`overview.${r.direction}`)}</td>
                                  <td>
                                    {r.amount} {r.unit}
                                  </td>
                                  <td>{instant(r.postedAt)}</td>
                                  <td className="break-all">{r.sourceId}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </section>
                      <section>
                        <h3 className="font-semibold">{t('overview.incomeSources')}</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr>
                                {['category', 'amount', 'status', 'source'].map((k) => (
                                  <th className="p-2 text-left" key={k}>
                                    {t(`overview.${k}`)}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {detail.income.map((r) => (
                                <tr className="border-t border-base-300" key={r.id}>
                                  <td>{t(`overview.${r.category}`)}</td>
                                  <td>{r.amount ?? t('overview.unavailable')} TWD</td>
                                  <td>{t(`overview.${r.settlementStatus}`)}</td>
                                  <td className="break-all">
                                    {r.sourceId} · v{r.sourceVersion ?? '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </section>
                      {!!detail.unclassifiedIncome?.length && (
                        <section>
                          <h3 className="font-semibold">{t('overview.unclassified')}</h3>
                          <p>{t('overview.unclassifiedNotice')}</p>
                          {detail.unclassifiedIncome.map((r) => (
                            <p className="break-all" key={r.id}>
                              {r.amount} TWD · {instant(r.postedAt)} · {r.id}
                            </p>
                          ))}
                        </section>
                      )}
                      {!!detail.lateEvidence?.length && (
                        <section>
                          <h3 className="font-semibold">{t('overview.lateEvidence')}</h3>
                          <p>{t('overview.lateNotice')}</p>
                          {detail.lateEvidence.map((r) => (
                            <p className="break-all" key={r.id}>
                              {instant(r.occurredAt)} · {r.kind} · {r.amount} {r.unit} · {r.id}
                            </p>
                          ))}
                        </section>
                      )}
                      {!!detail.historicalSettlementRows?.length && (
                        <section>
                          <h3 className="font-semibold">{t('overview.historicalEvidence')}</h3>
                          <p>{t('overview.historicalNotice')}</p>
                          {detail.historicalSettlementRows.map((r) => (
                            <p key={r.id}>
                              {r.memberName} · {r.amount} {r.unit} ·{' '}
                              {t(`overview.${r.metadata?.origin ?? 'UNKNOWN'}`)} · v
                              {r.sourceVersion} · {r.sourceId} · {r.metadata?.note}
                            </p>
                          ))}
                        </section>
                      )}
                    </div>
                  </details>
                </>
              )}
              {!detail && !detailQuery.isFetching && !detailQuery.isError && <p>{t('noData')}</p>}
            </section>
          )}
        </>
      )}
    </AdminPage>
  );
}
